import type { AssetBulk, AssetBulkResult, AssetCounts, AssetSort, AssetStatus, AssetStatusFilter, AssetSummary } from '@lastrouter/contracts';
import {
  Alert,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Button,
  Checkbox,
  ChevronLeft,
  ChevronRight,
  cn,
  Dialog,
  DialogContent,
  Field,
  focusRing,
  Input,
  NativeSelect,
  RefreshCw,
  Search,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Unlink,
  X,
} from '@lastrouter/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type FormEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { errorMessage, useApiMutation, useApiQuery } from '../../api/hooks.js';
import { useSession } from '../../app/session.js';
import { EmptyState, ErrorState, LoadingBlock, StatusBadge } from '../../components/common.js';
import { formatDateTime, formatNumber } from '../../lib/format.js';
import { APP_PAGE_SIZES, APP_STATUSES, appsQuery, hasFilters, nextSort, type AppsSearch } from '../assets/logic.js';

const ASSET_TONE = { nao_habilitado: 'neutral', rascunho: 'warning', publicado: 'success' } as const;

export function AssetStatusBadge({ status }: { status: AssetStatus }) {
  const { t } = useTranslation();
  return <StatusBadge tone={ASSET_TONE[status]}>{t(`assets.statuses.${status}`)}</StatusBadge>;
}

/** O que a lista invalida depois de uma mudança em apps. */
export const APPS_LIST_QUERIES = ['searchAssets', 'assets', 'clients', 'client', 'asset'] as const;

type SetSearch = (patch: Partial<AppsSearch>, replace?: boolean) => void;

/** Busca com espera curta: a URL (e a consulta) só muda quando a pessoa para de digitar. */
export function useDebouncedText(value: string | undefined, onSettle: (v: string) => void, ms = 300) {
  const [text, setText] = useState(value ?? '');
  // Voltar do navegador ou "limpar" trocam o valor da URL; o espaço que a pessoa acabou de digitar fica.
  useEffect(() => setText((cur) => (cur.trim() === (value ?? '') ? cur : (value ?? ''))), [value]);
  useEffect(() => {
    if (text.trim() === (value ?? '')) return;
    const h = setTimeout(() => onSettle(text.trim()), ms);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return [text, setText] as const;
}

const STATUS_COUNT: Record<AssetStatusFilter, keyof AssetCounts> = { published: 'published', draft: 'draft', not_enabled: 'not_enabled', missing: 'missing' };

function StatusChips({ counts, active, onPick }: { counts: AssetCounts; active: AssetStatusFilter | undefined; onPick: (s: AssetStatusFilter | undefined) => void }) {
  const { t } = useTranslation();
  const chip = (key: AssetStatusFilter | undefined, label: string, n: number) => (
    <button
      key={key ?? 'all'}
      type="button"
      aria-pressed={active === key}
      onClick={() => onPick(key)}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[length:var(--lr-text-sm)] transition-colors',
        active === key ? 'border-accent bg-accent-soft font-medium text-fg' : 'border-border bg-surface text-fg-secondary hover:bg-sunken hover:text-fg',
        focusRing,
      )}
    >
      {label}
      <span className="tabular text-fg-muted">{formatNumber(n)}</span>
    </button>
  );
  return (
    <div role="group" aria-label={t('apps.statusFilter')} className="flex flex-wrap gap-2">
      {chip(undefined, t('apps.all'), counts.all)}
      {APP_STATUSES.filter((s) => s !== 'missing' || counts.missing > 0 || active === 'missing').map((s) => chip(s, t(`apps.statusFilters.${s}`), counts[STATUS_COUNT[s]]))}
    </div>
  );
}

type SortField = 'name' | 'status' | 'client' | 'last_reload';

function SortHead({ field, label, sort, onSort, className }: { field: SortField; label: string; sort: AssetSort | undefined; onSort: (s: AssetSort) => void; className?: string }) {
  const cur = sort ?? 'name';
  const dir = cur === field ? 'ascending' : cur === `-${field}` ? 'descending' : 'none';
  const Icon = dir === 'ascending' ? ArrowUp : dir === 'descending' ? ArrowDown : ArrowUpDown;
  return (
    <TableHead aria-sort={dir} className={className}>
      <button type="button" onClick={() => onSort(nextSort(sort, field))} className={cn('-mx-1 inline-flex items-center gap-1 rounded-[var(--lr-radius-sm)] px-1 hover:text-fg', focusRing)}>
        {label}
        <Icon className={cn('size-3.5', dir === 'none' && 'opacity-40')} aria-hidden="true" />
      </button>
    </TableHead>
  );
}

function VersionText({ a }: { a: AssetSummary }) {
  const { t } = useTranslation();
  const parts = [a.published_number === null ? null : t('apps.liveVersion', { n: a.published_number }), a.draft_number === null ? null : t('apps.draftVersion', { n: a.draft_number })].filter(Boolean);
  return <>{parts.length ? parts.join(', ') : '-'}</>;
}

/** Ações da tela: as do servidor e "remover do cliente", que é set_client com cliente nulo. */
export type BulkAction = AssetBulk['action'] | 'unlink';

/** Ação em lote com confirmação. Roda no servidor, um app por vez, e conta o que mudou. */
export function BulkDialog({ action, assets, onClose, onDone }: { action: BulkAction; assets: AssetSummary[]; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const clients = useApiQuery('clients', undefined, { enabled: action === 'set_client' });
  const bulk = useApiMutation('bulkAssets', [...APPS_LIST_QUERIES]);
  const [client, setClient] = useState('');
  const [result, setResult] = useState<AssetBulkResult | null>(null);
  const affected =
    action === 'enable'
      ? assets.filter((a) => a.status === 'nao_habilitado')
      : action === 'disable'
        ? assets.filter((a) => a.status !== 'nao_habilitado')
        : action === 'unlink'
          ? assets.filter((a) => a.client_id !== null)
          : assets;
  const published = action === 'disable' ? affected.filter((a) => a.status === 'publicado').length : 0;
  const chosen = client || clients.data?.[0]?.id || '';

  function submit(e: FormEvent) {
    e.preventDefault();
    const ids = affected.map((a) => a.id);
    const body: AssetBulk =
      action === 'unlink' ? { action: 'set_client', ids, client_id: null } : { action, ids, ...(action === 'set_client' ? { client_id: chosen === 'none' ? null : chosen } : {}) };
    bulk.mutate(
      { body },
      {
        onSuccess: (r) => {
          if (r.failed.length) setResult(r);
          else {
            toast.success(t('apps.bulk.done', { count: r.changed }));
            onDone();
          }
        },
        onError: (err) => toast.error(errorMessage(t, err)),
      },
    );
  }

  const shown = affected.slice(0, 8);
  return (
    <Dialog open onOpenChange={(o) => !o && (result ? onDone() : onClose())}>
      <DialogContent title={t(`apps.bulk.${action}Title`, { count: affected.length })} description={t(`apps.bulk.${action}Help`)} closeLabel={t('common.close')}>
        {result ? (
          <div className="flex flex-col gap-3">
            <Alert tone="warning" title={t('apps.bulk.partial', { changed: result.changed, failed: result.failed.length })}>
              <ul className="list-disc pl-5">
                {result.failed.map((f) => (
                  <li key={f.id}>
                    <span className="font-medium">{f.name ?? f.id}</span>: {f.message}
                  </li>
                ))}
              </ul>
            </Alert>
            <div className="flex justify-end">
              <Button variant="primary" onClick={onDone}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={submit}>
            {affected.length === 0 ? (
              <p className="text-fg-secondary">{t(`apps.bulk.${action}Nothing`)}</p>
            ) : (
              <>
                <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-[var(--lr-radius-md)] bg-sunken p-3 text-[length:var(--lr-text-sm)]">
                  {shown.map((a) => (
                    <li key={a.id} className="truncate">
                      {a.name}
                    </li>
                  ))}
                  {affected.length > shown.length ? <li className="text-fg-muted">{t('apps.bulk.andMore', { count: affected.length - shown.length })}</li> : null}
                </ul>
                {affected.length < assets.length ? <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t(`apps.bulk.${action}Skipped`, { count: assets.length - affected.length })}</p> : null}
                {published ? <Alert tone="warning" title={t('apps.bulk.disablePublished', { count: published })} /> : null}
              </>
            )}
            {action === 'set_client' ? (
              <Field id="lote-cliente" label={t('apps.bulk.client')}>
                <NativeSelect value={chosen} onChange={(e) => setClient(e.target.value)}>
                  {clients.data?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                  <option value="none">{t('apps.bulk.noClient')}</option>
                </NativeSelect>
              </Field>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button onClick={onClose}>{t('common.cancel')}</Button>
              <Button type="submit" variant={action === 'disable' ? 'danger' : 'primary'} disabled={affected.length === 0 || bulk.isPending || (action === 'set_client' && !chosen)}>
                {t(`apps.bulk.${action}Confirm`, { count: affected.length })}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Lista de apps para quem tem centenas: busca, filtros, contagem por situação, ordem e página no servidor,
 * com o estado todo na URL. Admin seleciona vários e faz a mesma ação em todos.
 * Dentro da tela de um cliente (fixedClient), o filtro e a coluna de cliente somem.
 */
export function AppsList({ search, setSearch, fixedClient }: { search: AppsSearch; setSearch: SetSearch; fixedClient?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = Boolean(useSession().data?.is_admin);
  const query = appsQuery(search, fixedClient);
  const q = useApiQuery('searchAssets', { query });
  const sources = useApiQuery('connections', undefined, { enabled: isAdmin });
  const [text, setText] = useDebouncedText(search.q, (v) => setSearch({ q: v || undefined, page: undefined }, true));
  const [selected, setSelected] = useState<Map<string, AssetSummary>>(new Map());
  const [bulk, setBulk] = useState<{ action: BulkAction; assets: AssetSummary[] } | null>(null);
  const startBulk = (action: BulkAction) => setBulk({ action, assets: [...selected.values()] });

  // Outra busca, filtro ou página: a seleção recomeça (ela vale para o que está na tela).
  const queryKey = JSON.stringify(query);
  useEffect(() => setSelected(new Map()), [queryKey]);

  const filtered = hasFilters(search, fixedClient);
  const clear = () => {
    setText('');
    setSearch({ q: undefined, status: undefined, source: undefined, client: undefined, page: undefined });
  };
  const page = q.data;
  const items = page?.items ?? [];
  const pages = page ? Math.max(1, Math.ceil(page.total / page.page_size)) : 1;
  const allOnPage = items.length > 0 && items.every((a) => selected.has(a.id));
  const someOnPage = items.some((a) => selected.has(a.id));
  const toggle = (a: AssetSummary, on: boolean) =>
    setSelected((m) => {
      const n = new Map(m);
      if (on) n.set(a.id, a);
      else n.delete(a.id);
      return n;
    });
  const toggleAll = (on: boolean) => setSelected(on ? new Map(items.map((a) => [a.id, a])) : new Map());
  const open = (e: MouseEvent, id: string) => {
    // Clique em link, botão ou caixa de seleção faz só o que eles fazem.
    if ((e.target as HTMLElement).closest('a,button,input,label,select')) return;
    void navigate({ to: '/apps/$id', params: { id } });
  };
  const sourceList = sources.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex min-w-0 flex-[2_1_260px] flex-col gap-1.5">
            <label htmlFor="apps-busca" className="text-[length:var(--lr-text-sm)] font-medium text-fg">
              {t('apps.search')}
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-muted" aria-hidden="true" />
              <Input id="apps-busca" type="search" className="pl-9" value={text} onChange={(e) => setText(e.target.value)} placeholder={t('apps.searchPlaceholder')} autoComplete="off" />
            </div>
          </div>
          {isAdmin && sourceList.length > 1 ? (
            <Field id="apps-fonte" label={t('apps.source')} className="min-w-0 flex-[1_1_150px]">
              <NativeSelect value={search.source ?? ''} onChange={(e) => setSearch({ source: e.target.value || undefined, page: undefined })}>
                <option value="">{t('apps.anySource')}</option>
                {sourceList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.id}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          ) : null}
          {/* No celular não há cabeçalho de tabela: a ordem fica num seletor. */}
          <Field id="apps-ordem" label={t('apps.sort')} className="min-w-0 flex-[1_1_150px] md:hidden">
            <NativeSelect value={search.sort ?? 'name'} onChange={(e) => setSearch({ sort: (e.target.value === 'name' ? undefined : e.target.value) as AssetSort | undefined, page: undefined })}>
              {(['name', '-name', 'status', '-last_reload'] as const).map((s) => (
                <option key={s} value={s}>
                  {t(`apps.sorts.${s.replace('-', 'desc_')}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          {filtered ? (
            <Button variant="ghost" onClick={clear}>
              <X />
              {t('apps.clear')}
            </Button>
          ) : null}
        </div>
        {page?.access_unavailable ? <Alert tone="warning" title={t('apps.accessUnavailable')} /> : null}
        {page ? <StatusChips counts={page.counts} active={search.status} onPick={(s) => setSearch({ status: s, page: undefined })} /> : null}
      </div>

      {q.isPending ? (
        <LoadingBlock rows={8} />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : page?.access_unavailable ? null : !page || page.total === 0 ? (
        filtered ? (
          <div className="flex flex-col items-center gap-3 rounded-[var(--card-radius)] bg-sunken px-4 py-8 text-center">
            <p className="text-fg-secondary">{search.q ? t('apps.noMatchFor', { q: search.q }) : t('apps.noMatch')}</p>
            <Button onClick={clear}>
              <X />
              {t('apps.clear')}
            </Button>
          </div>
        ) : (
          <EmptyState>{fixedClient ? t('clients.noApps') : t('apps.emptyMine')}</EmptyState>
        )
      ) : (
        <div aria-busy={q.isPlaceholderData} className={cn('lr-fade flex flex-col gap-3', q.isPlaceholderData && 'lr-stale')}>
          {/* Celular: um cartão por app, sem rolagem para o lado. */}
          <ul aria-label={t('apps.caption', { count: page.total })} className="flex flex-col gap-2 md:hidden">
            {items.map((a) => (
              <li key={a.id} className="relative flex gap-3 rounded-[var(--card-radius)] border border-border bg-surface p-3 has-[a:hover]:bg-sunken/60">
                {isAdmin ? <Checkbox className="relative z-10 mt-1" aria-label={t('apps.select', { name: a.name })} checked={selected.has(a.id)} onChange={(e) => toggle(a, e.target.checked)} /> : null}
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  {/* O link cobre o cartão inteiro: tocar em qualquer lugar abre o app. */}
                  <Link to="/apps/$id" params={{ id: a.id }} className={cn('break-words font-medium text-fg after:absolute after:inset-0 after:rounded-[var(--card-radius)]', focusRing)}>
                    {a.name}
                  </Link>
                  <div className="flex flex-wrap gap-1.5">
                    <AssetStatusBadge status={a.status} />
                    {a.missing ? <StatusBadge tone="danger">{t('assets.missing')}</StatusBadge> : null}
                  </div>
                  {a.space ? <p className="break-words text-[length:var(--lr-text-sm)] text-fg-secondary">{a.space}</p> : null}
                  <p className="flex flex-wrap gap-x-3 text-[length:var(--lr-text-xs)] text-fg-muted">
                    <span>{t('apps.reloadedAt', { when: formatDateTime(a.last_reload_at) })}</span>
                    {a.published_number !== null || a.draft_number !== null ? (
                      <span>
                        <VersionText a={a} />
                      </span>
                    ) : null}
                  </p>
                  {isAdmin && fixedClient ? (
                    <div className="relative z-10">
                      <Button size="sm" variant="ghost" className="-ml-3" aria-label={t('apps.unlinkOne', { name: a.name })} onClick={() => setBulk({ action: 'unlink', assets: [a] })}>
                        <Unlink />
                        {t('apps.bulk.unlink')}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
          <div className="hidden md:block">
            <Table stickyHeader>
              <TableCaption>{t('apps.caption', { count: page.total })}</TableCaption>
              <TableHeader>
                <TableRow>
                  {isAdmin ? (
                    <TableHead className="w-10">
                      <Checkbox
                        aria-label={t('apps.selectPage')}
                        checked={allOnPage}
                        ref={(el) => {
                          if (el) el.indeterminate = someOnPage && !allOnPage;
                        }}
                        onChange={(e) => toggleAll(e.target.checked)}
                      />
                    </TableHead>
                  ) : null}
                  <SortHead field="name" label={t('apps.name')} sort={search.sort} onSort={(s) => setSearch({ sort: s === 'name' ? undefined : s, page: undefined })} />
                  <SortHead field="status" label={t('apps.status')} sort={search.sort} onSort={(s) => setSearch({ sort: s, page: undefined })} />
                  <TableHead>{t('apps.version')}</TableHead>
                  <SortHead field="last_reload" label={t('assets.lastReload')} sort={search.sort} onSort={(s) => setSearch({ sort: s, page: undefined })} />
                  {isAdmin && fixedClient ? (
                    <TableHead>
                      <span className="sr-only">{t('common.actions')}</span>
                    </TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a.id} onClick={(e) => open(e, a.id)} className={cn('cursor-pointer', selected.has(a.id) && 'bg-accent-soft/60')}>
                    {isAdmin ? (
                      <TableCell>
                        <Checkbox aria-label={t('apps.select', { name: a.name })} checked={selected.has(a.id)} onChange={(e) => toggle(a, e.target.checked)} />
                      </TableCell>
                    ) : null}
                    <TableCell className="max-w-[420px]">
                      <Link to="/apps/$id" params={{ id: a.id }} className={cn('break-words font-medium text-fg underline-offset-4 hover:underline', focusRing)}>
                        {a.name}
                      </Link>
                      {a.space ? <p className="truncate text-[length:var(--lr-text-sm)] text-fg-muted">{a.space}</p> : null}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <AssetStatusBadge status={a.status} />
                        {a.missing ? <StatusBadge tone="danger">{t('assets.missing')}</StatusBadge> : null}
                      </div>
                    </TableCell>
                    <TableCell className="tabular whitespace-nowrap text-[length:var(--lr-text-sm)]">
                      <VersionText a={a} />
                    </TableCell>
                    <TableCell className="tabular whitespace-nowrap text-[length:var(--lr-text-sm)]">{formatDateTime(a.last_reload_at)}</TableCell>
                    {isAdmin && fixedClient ? (
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" aria-label={t('apps.unlinkOne', { name: a.name })} onClick={() => setBulk({ action: 'unlink', assets: [a] })}>
                          <Unlink />
                          {t('apps.bulk.unlink')}
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <nav aria-label={t('apps.pagination')} className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-[length:var(--lr-text-sm)] text-fg-secondary" aria-live="polite">
              {t('apps.range', { from: (page.page - 1) * page.page_size + 1, to: (page.page - 1) * page.page_size + items.length, total: formatNumber(page.total) })}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-[length:var(--lr-text-sm)] text-fg-secondary max-sm:hidden">
                {t('apps.perPage')}
                <NativeSelect className="w-24" value={page.page_size} onChange={(e) => setSearch({ size: Number(e.target.value) === APP_PAGE_SIZES[0] ? undefined : Number(e.target.value), page: undefined })}>
                  {APP_PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </NativeSelect>
              </label>
              <Button size="sm" disabled={page.page <= 1} onClick={() => setSearch({ page: page.page - 1 > 1 ? page.page - 1 : undefined })}>
                <ChevronLeft />
                {t('apps.prev')}
              </Button>
              <span className="tabular text-[length:var(--lr-text-sm)]">{t('apps.pageOf', { page: page.page, pages })}</span>
              <Button size="sm" disabled={page.page >= pages} onClick={() => setSearch({ page: page.page + 1 })}>
                {t('apps.next')}
                <ChevronRight />
              </Button>
            </div>
          </nav>
        </div>
      )}

      {isAdmin && selected.size > 0 ? (
        <div role="region" aria-label={t('apps.bulk.label')} className="lr-enter sticky bottom-3 z-20 flex flex-wrap items-center gap-2 rounded-[var(--card-radius)] border border-border-strong bg-surface p-2 pl-4 shadow-lg">
          <span className="mr-auto font-medium" aria-live="polite">
            {t('apps.bulk.selected', { count: selected.size })}
          </span>
          {fixedClient ? (
            <>
              <Button size="sm" variant="primary" onClick={() => startBulk('set_client')}>
                {t('apps.bulk.moveClient')}
              </Button>
              <Button size="sm" onClick={() => startBulk('unlink')}>
                {t('apps.bulk.unlink')}
              </Button>
            </>
          ) : null}
          <Button size="sm" onClick={() => startBulk('enable')}>
            {t('apps.bulk.enable')}
          </Button>
          <Button size="sm" onClick={() => startBulk('disable')}>
            {t('apps.bulk.disable')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Map())}>
            {t('apps.bulk.clearSelection')}
          </Button>
        </div>
      ) : null}
      {bulk ? (
        <BulkDialog
          action={bulk.action}
          assets={bulk.assets}
          onClose={() => setBulk(null)}
          onDone={() => {
            setBulk(null);
            setSelected(new Map());
          }}
        />
      ) : null}
    </div>
  );
}

/** Escolher apps sem cliente para ligar a este: busca no servidor e seleção múltipla. */
export function AddAppsDialog({ clientId, clientName, onClose }: { clientId: string; clientName: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(h);
  }, [q]);
  const list = useApiQuery('searchAssets', { query: { client_id: 'none', page_size: 50, ...(debounced ? { q: debounced } : {}) } });
  const bulk = useApiMutation('bulkAssets', [...APPS_LIST_QUERIES]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const items = useMemo(() => list.data?.items ?? [], [list.data]);

  function submit(e: FormEvent) {
    e.preventDefault();
    bulk.mutate(
      { body: { action: 'set_client', ids: [...picked], client_id: clientId } },
      {
        onSuccess: (r) => {
          toast.success(t('clients.appsAdded', { count: r.changed }));
          onClose();
        },
        onError: (err) => toast.error(errorMessage(t, err)),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('clients.addAppTitle', { name: clientName })} description={t('clients.addAppHelp')} closeLabel={t('common.close')} className="w-[min(640px,calc(100vw-32px))]">
        <form className="flex flex-col gap-3" onSubmit={submit}>
          <Field id="adicionar-busca" label={t('apps.search')}>
            <Input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('apps.searchPlaceholder')} autoFocus autoComplete="off" />
          </Field>
          {list.isPending ? (
            <LoadingBlock rows={3} />
          ) : list.error ? (
            <ErrorState error={list.error} />
          ) : items.length === 0 ? (
            <p className="text-fg-secondary">{debounced ? t('apps.noMatchFor', { q: debounced }) : t('clients.noUnassigned')}</p>
          ) : (
            <ul className={cn('flex max-h-80 flex-col overflow-y-auto rounded-[var(--lr-radius-md)] border border-border', list.isPlaceholderData && 'lr-stale')}>
              {items.map((a) => (
                <li key={a.id} className="border-b border-border last:border-0">
                  <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-sunken">
                    <Checkbox
                      checked={picked.has(a.id)}
                      onChange={(e) =>
                        setPicked((s) => {
                          const n = new Set(s);
                          if (e.target.checked) n.add(a.id);
                          else n.delete(a.id);
                          return n;
                        })
                      }
                    />
                    <span className="flex min-w-0 flex-col">
                      <span className="break-words">{a.name}</span>
                      {a.space ? <span className="text-[length:var(--lr-text-xs)] text-fg-muted">{a.space}</span> : null}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {list.data && list.data.total > items.length ? <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t('clients.refineSearch', { count: list.data.total })}</p> : null}
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="primary" disabled={picked.size === 0 || bulk.isPending}>
              {t('clients.addApps', { count: picked.size })}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SyncDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const sources = useApiQuery('connections');
  const sync = useApiMutation('syncAssets', [...APPS_LIST_QUERIES]);
  const [source, setSource] = useState('');
  const chosen = source || sources.data?.[0]?.id || '';

  function submit(e: FormEvent) {
    e.preventDefault();
    if (chosen) sync.mutate({ body: { source_id: chosen } });
  }

  const r = sync.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('clients.syncTitle')} description={t('clients.syncHelp')} closeLabel={t('common.close')}>
        {sources.error ? <ErrorState error={sources.error} onRetry={() => void sources.refetch()} /> : null}
        {sources.data && !sources.data.length ? <p className="text-fg-secondary">{t('clients.noSources')}</p> : null}
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <Field id="sync-fonte" label={t('clients.source')}>
            <NativeSelect value={chosen} onChange={(e) => setSource(e.target.value)} disabled={!sources.data?.length}>
              {sources.data?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.id} ({s.type})
                </option>
              ))}
            </NativeSelect>
          </Field>
          <div>
            <Button type="submit" variant="primary" disabled={!chosen || sync.isPending}>
              {t('clients.runSync')}
            </Button>
          </div>
        </form>
        <div aria-live="polite" className="mt-4 empty:hidden">
          {sync.error ? <ErrorState error={sync.error} /> : null}
          {r ? <Alert tone={r.missing ? 'warning' : 'success'} title={t('clients.syncResult', { user: r.listed_as, added: r.added, updated: r.updated, missing: r.missing })} /> : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Botão "Atualizar lista do Qlik" com o diálogo de escolher a fonte. */
export function SyncButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <RefreshCw />
        {t('clients.sync')}
      </Button>
      {open ? <SyncDialog onClose={() => setOpen(false)} /> : null}
    </>
  );
}

